import React from 'react';
import { StatusText } from '../Card';
import Icon from '@xo-union/tk-component-icons';
import { Body1, Caption } from '@xo-union/tk-ui-typography';
import { format, parse, parseISO } from 'date-fns';
import { DisplayLink, DisplayButton } from '@xo-union/tk-ui-links';
import { EventsQuery } from '../../generated/graphql';
import { weddingVenuesRoute } from '../../helpers/routes';
import styles from './eventList.scss';
import { AutoFocusableFields } from './helper';

export type EventType = NonNullable<NonNullable<EventsQuery['events']>[0]>;

interface Props {
  event: EventType;
  onClick: (event: EventType & { requireRsvp?: boolean }, autoFocusField: AutoFocusableFields | undefined) => void;
}

const formatDate = (date: string) => format(parseISO(date), 'EEEE, LLL. d');

const formatTime = (time: string) => format(parse(time, 'HH:mm', new Date()), 'p');

const EventCard: React.FC<Props> = ({ event, onClick }) => {
  const navigate = (params?: { autoFocusField: AutoFocusableFields }) =>
    onClick(event as EventType & { requireRsvp?: boolean }, params?.autoFocusField);
  const handleBrowseVenue = () => {
    if (window.analytics) {
      // We use the Segment track callback to ensure the tracking request is not interrupted
      // upon page navigation.
      window.analytics.track('WWS Dashboard Interaction', {
        action: 'browse venues',
        experimentName: 'Browse venue experiment',
        experimentVariant: 'test',
      });
    }
  };

  return (
    <div className={`${styles.baseTile} ${styles.event}`}>
      <div className={styles.eventHeaderWrapper}>
        <div className={styles.eventHeader}>
          <Body1 bold as="span" className={styles.eventName}>
            {event.name}
          </Body1>

          {event.visible !== true && (
            <div className={styles.statusText}>
              <StatusText>Private</StatusText>
            </div>
          )}
        </div>
        <div>
          <DisplayButton
            type="button"
            className={styles.eventHeadEdit}
            tabIndex={0}
            data-testid={`event-edit-${event.id}`}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.keyCode === 13) {
                navigate();
              }
            }}
            onClick={navigate}
          >
            <Icon name="edit" size="md" className={styles.event__head__edit__icon} />
          </DisplayButton>
        </div>
      </div>
      <div className={styles.eventBody}>
        <Caption as="div" className={styles.eventBodyCaption}>
          <Icon name="date" size="sm" />{' '}
          {event.date ? (
            `${formatDate(event.date)}`
          ) : (
            <DisplayButton onClick={() => navigate({ autoFocusField: 'date' })}>Add date</DisplayButton>
          )}
        </Caption>

        <Caption as="div" className={styles.eventBodyCaption}>
          <Icon name="time" size="sm" />{' '}
          {event.time ? (
            `${formatTime(event.time)}${event.endTime ? ` - ${formatTime(event.endTime)}` : ''}`
          ) : (
            <DisplayButton onClick={() => navigate({ autoFocusField: 'time' })}>Add time</DisplayButton>
          )}
        </Caption>

        {
          <Caption as="div" className={styles.eventBodyCaption}>
            <Icon name="address" size="sm" />{' '}
            {event.location?.name ? (
              event.location.name
            ) : (
              <>
                <DisplayButton onClick={() => navigate({ autoFocusField: 'venue' })}>Add venue</DisplayButton>
                <span className={styles.eventBodySeparator}>|</span>
                <DisplayLink
                  className={styles.linkOut}
                  tabIndex={0}
                  onKeyDown={(e: KeyboardEvent) => {
                    if (e.keyCode === 13) {
                      handleBrowseVenue();
                    }
                  }}
                  onClick={handleBrowseVenue}
                  href={weddingVenuesRoute}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="md"
                  labelDirection="left"
                  iconName="linkout"
                  color="secondary"
                >
                  Browse venues
                </DisplayLink>
              </>
            )}
          </Caption>
        }
      </div>
    </div>
  );
};

export default EventCard;
